import type { CSSProperties } from "react";
import Image from "next/image";
import {
  ACCENT,
  ACCENT_TEXT,
  DARK,
  DISPLAY,
  GROUND,
  INK,
  LABEL_CLS,
  NOISE_DATA_URI,
  mix,
} from "./theme";

/* ================================================================= */
/*  Texture                                                            */
/* ================================================================= */

export function Grain({ opacity = 0.5 }: { opacity?: number }) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 mix-blend-soft-light"
      style={{ backgroundImage: `url("${NOISE_DATA_URI}")`, opacity }}
    />
  );
}

/* ================================================================= */
/*  Oversized background numeral                                       */
/* ================================================================= */

export function BgNumeral({
  numeral,
  dark = false,
}: {
  numeral: string;
  dark?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-end justify-end overflow-hidden"
    >
      <span
        style={{
          ...DISPLAY,
          color: dark ? ACCENT : DARK,
          opacity: 0.07,
          letterSpacing: "-0.06em",
        }}
        className="-mr-10 -mb-12 select-none text-[18rem] font-semibold leading-[0.78] md:-mr-16 md:-mb-20 md:text-[28rem]"
      >
        {numeral}
      </span>
    </div>
  );
}

/* ================================================================= */
/*  Section marker — dot + uppercase tracked label                    */
/* ================================================================= */

export function SectionMarker({
  section,
  label,
  dark = false,
}: {
  section: string;
  label: string;
  dark?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="h-2 w-2"
        style={{ backgroundColor: dark ? ACCENT : DARK }}
        aria-hidden="true"
      />
      <span style={{ color: dark ? ACCENT : ACCENT_TEXT }} className={LABEL_CLS}>
        {section ? `${section} · ${label}` : label}
      </span>
    </div>
  );
}

/* ================================================================= */
/*  Hero / closing metadata cell                                      */
/* ================================================================= */

export function HeroMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center sm:text-center">
      <p
        style={{ color: mix("--theme-ground", 80) }}
        className={`mb-1.5 ${LABEL_CLS}`}
      >
        {label}
      </p>
      <p className="text-[0.95rem]" style={{ color: mix("--theme-ground", 88) }}>
        {value}
      </p>
    </div>
  );
}

/* ================================================================= */
/*  Logo grid — shared between hero (dark) and trust (light)           */
/* ================================================================= */

export type Client = {
  name: string;
  /** Image source. Light-variant (dark artwork on transparent) so we
   *  can invert it on dark contexts via CSS. */
  src?: string;
  /** Fallback wordmark when no image file is available. Renders as
   *  two-line bold caps inside the same cell. */
  text?: { line1: string; line2?: string };
  /** Square / vertical-leaning logos render very narrow when capped by
   *  height alone. Setting `tall` gives them a larger max-height and a
   *  tighter max-width so they read at the same visual weight as the
   *  wider wordmark logos in adjacent cells. */
  tall?: boolean;
  subtitle: string;
};

export function LogoGrid({
  variant,
  clients,
}: {
  /** "dark" inverts the light-variant artwork to white on the deep
   *  field, with no cell background or separators (blends into the
   *  hero). "light" renders the dark artwork as-is over the cream
   *  ground with hairline separators between cells. */
  variant: "dark" | "light";
  clients: Client[];
}) {
  const isDark = variant === "dark";
  const textColor = isDark ? GROUND : INK;
  // Force uniform colour treatment on both variants so coloured source
  // artwork doesn't read as a different "category" from the monochrome
  // government coats-of-arms in neighbouring cells.
  //   dark variant  → brightness(0) invert(1)  pure white
  //   light variant → brightness(0)            pure black
  const imgFilter = isDark ? "brightness(0) invert(1)" : "brightness(0)";

  const ulClass = isDark
    ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
    : "grid grid-cols-2 gap-px sm:grid-cols-3 md:grid-cols-4";
  const ulStyle: CSSProperties = isDark
    ? {}
    : { backgroundColor: mix("--theme-ink", 12) };
  const liStyle: CSSProperties = isDark ? {} : { backgroundColor: GROUND };

  // Logos are intentionally dim by default so the hero h1 carries the
  // visual hierarchy. Hovering any cell brightens that single logo.
  const dimOpacity = isDark ? 0.4 : 0.45;
  const hoverOpacity = isDark ? 1 : 0.95;

  return (
    <ul className={ulClass} style={ulStyle}>
      {clients.map((c) => (
        <li
          key={c.name}
          className="group flex aspect-[2/1] items-center justify-center px-3 py-6 sm:px-5 sm:py-7 md:aspect-[2.2/1] md:px-6 md:py-8"
          style={liStyle}
        >
          {c.src ? (
            <Image
              src={c.src}
              alt={`${c.name} logo`}
              width={180}
              height={80}
              className="h-14 w-[120px] object-contain transition-opacity duration-300 ease-out group-hover:opacity-[var(--logo-hover)] sm:h-16 sm:w-[150px] md:h-20 md:w-[180px]"
              style={
                {
                  filter: imgFilter,
                  opacity: dimOpacity,
                  ["--logo-hover" as string]: String(hoverOpacity),
                } as CSSProperties
              }
            />
          ) : c.text ? (
            <div
              className="flex h-14 w-[120px] flex-col items-center justify-center leading-[1.05] transition-opacity duration-300 ease-out group-hover:opacity-[var(--logo-hover)] sm:h-16 sm:w-[150px] md:h-20 md:w-[180px]"
              style={
                {
                  ...DISPLAY,
                  color: textColor,
                  opacity: dimOpacity,
                  ["--logo-hover" as string]: String(hoverOpacity),
                } as CSSProperties
              }
            >
              <span className="text-[0.82rem] font-semibold tracking-[-0.015em] sm:text-[1rem] md:text-[1.2rem]">
                {c.text.line1}
              </span>
              {c.text.line2 && (
                <span className="text-[0.82rem] font-semibold tracking-[-0.015em] sm:text-[1rem] md:text-[1.2rem]">
                  {c.text.line2}
                </span>
              )}
            </div>
          ) : null}
        </li>
      ))}
      {isDark ? null : <LogoGridFillers count={clients.length} />}
    </ul>
  );
}

/**
 * Blank cells that complete the light grid's last row at each breakpoint
 * (2, 3 and 4 columns). Its hairlines are the grid's background showing
 * through 1px gaps, so an incomplete row would otherwise end in a solid
 * block of that background.
 */
function LogoGridFillers({ count }: { count: number }) {
  const missing = { base: (2 - (count % 2)) % 2, sm: (3 - (count % 3)) % 3, md: (4 - (count % 4)) % 4 };
  const fillers = Math.max(missing.base, missing.sm, missing.md);
  return Array.from({ length: fillers }, (_, index) => (
    <li
      key={`filler-${index}`}
      aria-hidden="true"
      className={[
        index < missing.base ? "block" : "hidden",
        index < missing.sm ? "sm:block" : "sm:hidden",
        index < missing.md ? "md:block" : "md:hidden",
      ].join(" ")}
      style={{ backgroundColor: GROUND }}
    />
  ));
}
